import type { Ref } from 'react';

import { NestError } from '../../nests/api.ts';

/**
 * A labelled text field (forms.css `.field`), with its error under it: the field is marked
 * invalid and described by the message, so moving back to it reads what's wrong (A11Y-8).
 * The sheets and the invite landing share it. The message is a polite alert; the form also
 * moves focus to the field the error is about.
 */
export function Field({
	label,
	value,
	onChange,
	max,
	id,
	code,
	autoComplete,
	enterKeyHint,
	inputRef,
	error,
}: {
	label: string;
	value: string;
	onChange: (v: string) => void;
	max: number;
	id: string;
	code?: boolean;
	autoComplete?: string;
	enterKeyHint?: 'next' | 'done' | 'go';
	inputRef?: Ref<HTMLInputElement>;
	/** The message when this field is what didn't take. */
	error?: string | null;
}) {
	const errId = `${id}-err`;
	return (
		<div className="fld">
			<label htmlFor={id}>{label}</label>
			<input
				id={id}
				ref={inputRef}
				className={code ? 'field field--code' : 'field'}
				value={value}
				maxLength={code ? 7 : max * 2}
				autoComplete={autoComplete ?? 'off'}
				autoCapitalize={code ? 'characters' : 'words'}
				autoCorrect="off"
				spellCheck={false}
				enterKeyHint={enterKeyHint}
				aria-invalid={error ? true : undefined}
				aria-describedby={error ? errId : undefined}
				onChange={(e) => onChange(e.target.value)}
			/>
			{error && (
				<p className="field-error" id={errId} role="alert">
					{error}
				</p>
			)}
		</div>
	);
}

/** A form's error that isn't about one field (the connection, the date): said once, in place. */
export function FormError({ text }: { text: string | null }) {
	if (!text) return null;
	return (
		<p className="field-error form-error" role="alert">
			{text}
		</p>
	);
}

/** ⌄, the nest switcher's and the Activity toggle's mark. */
export function Chevron({ className = 'pick-chev' }: { className?: string }) {
	return (
		<svg className={className} viewBox="0 0 24 24" aria-hidden="true">
			<path d="M6 9.5l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}

/** The feathers mark: a vermilion feather on an ink quill. */
export function Feather() {
	return (
		<svg className="feather" viewBox="0 0 24 24" aria-hidden="true">
			<path className="feather-q" d="M20 3C12 4 6 9 5 18l-1.5 3" />
			<path className="feather-v" d="M20 3c-1 7-5 13-14 14 1-7 6-12 14-14z" />
		</svg>
	);
}

export function InfoMark() {
	return (
		<svg className="fx-i" viewBox="0 0 24 24" aria-hidden="true">
			<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
			<path d="M12 11v6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
			<circle cx="12" cy="7.6" r="1.4" fill="currentColor" />
		</svg>
	);
}

/** The ink tick beside the nest you're looking at, in the switcher. */
export function TickOn() {
	return (
		<span className="tick-on" aria-hidden="true">
			<span className="tick">
				<svg viewBox="0 0 16 16">
					<path d="M3.5 8.5l3 3 6-7" />
				</svg>
			</span>
		</span>
	);
}

/** The longest names the api takes (docs/NESTS.md, "Names"). */
export const NEST_NAME_MAX = 30;
export const DISPLAY_NAME_MAX = 20;

/** A quick check before asking the api (which has the full rules, apps/api/src/nests/names.ts): empty, too long, or nothing to read. */
export function nameProblem(raw: string, max: number, what: 'nest' | 'you'): string | null {
	const name = raw.trim();
	if (!name) return what === 'nest' ? 'Give the nest a name.' : 'Add your name, as the nest will see it.';
	if ([...name].length > max) return `That’s too long: ${max} characters at most.`;
	if (!/[\p{L}\p{N}\p{Extended_Pictographic}]/u.test(name)) return NAME_NEEDS;
	return null;
}

const NAME_NEEDS = 'A name needs a letter, a number or an emoji.';

/** Which field a failed nest call is about: the nest's name, your name, or neither. */
export function nestErrorField(err: unknown): 'nest' | 'you' | null {
	if (!(err instanceof NestError)) return null;
	const msg = err.message.toLowerCase();
	if (err.kind === 'bad-request') {
		if (msg.startsWith('displayname')) return 'you';
		if (msg.startsWith('name')) return 'nest';
		return null;
	}
	if (err.kind === 'conflict' && msg.includes('already has that name')) return 'you';
	return null;
}

/** What a failed nest call says, in the app's voice. Never the raw message. */
export function nestErrorLine(err: unknown, action: 'start' | 'join' | 'load' | 'save'): string {
	if (!(err instanceof NestError)) return 'Something went wrong. Try again.';
	const msg = err.message.toLowerCase();
	switch (err.kind) {
		case 'network':
		case 'invalid':
			return err.status === 429 ? 'That’s a lot of tries. Wait a minute, then try again.' : 'Couldn’t reach Sparrow. Check your connection, then try again.';
		case 'bad-request':
			if (msg.includes('date')) return 'Your phone’s date looks wrong. Check it, then try again.';
			if (msg.includes('code')) return 'A code is 6 letters or numbers.';
			if (msg.includes('may use only')) return 'Use letters, numbers, spaces and simple punctuation.';
			if (msg.includes('at most')) return 'That name is too long.';
			if (msg.includes('needs a letter')) return NAME_NEEDS;
			return 'That didn’t work. Check the names, then try again.';
		case 'conflict':
			if (msg.includes('already has that name')) return 'Someone in this nest already has that name. Try another.';
			if (msg.includes('full')) return 'That nest is full: a nest holds up to 30 people.';
			if (msg.includes('at most')) return 'You’re in 10 nests already. Leave one first.';
			return 'That didn’t work. Try again.';
		case 'forbidden':
			return action === 'join' ? 'You can’t join this nest again.' : 'You can’t do that in this nest.';
		case 'not-found':
			return action === 'join' ? 'No nest has that code. Check it with whoever gave it to you.' : 'That nest isn’t there any more.';
	}
}
